import React, {useEffect, useState} from "react";
import styles from "./Footer.module.scss";
import {Button, ButtonType, Icon} from "@reapptor-apps/reapptor-react-components";
import Localizer from "@/localization/Localizer";
import {ApiProvider, PageRouteProvider, type PageRoute} from "@reapptor-apps/reapptor-react-common";
import PageDefinitions from "@/providers/PageDefinitions";
import _ from "lodash";
import PlayStoreBadge from "@/images/Google_Play_Store_badge_EN.svg";
import AppStoreBadge from "@/images/Download_on_the_App_Store_Badge.svg";
import {DynamicPageResponseSingleType} from "@/models/cms/DynamicPageResponse";

interface FooterLocation {
    id: string;
    name: string;
    address: string;
    postalCode: string;
    city: string;
}

interface OpeningHoursData {
    titleMondayWednesdayThursday: string;
    titleTuesdayFriday: string;
    titleSaturday: string;
    hoursMondayWednesdayThursday: string;
    hoursTuesdayFriday: string;
    hoursSaturday: string;
    
}

export interface LocationsData {
    attributes: {
        name: string;
        externalId: string;
        address: string;
        postalCode: string;
        town: string;
        openingHours: LocationOpeningHoursObject | null;
        phoneNumber: string | null;
    }
}

interface LocationOpeningHoursObject {
    TitleHoursPair: TitleHoursPair[];
}

interface TitleHoursPair {
    Title: string;
    Hours: string;
}

interface FooterProps {
    locations?: LocationsData[];
    onLocationClick?: (location: FooterLocation) => void
    onViewAllOpeningTimesClick?: () => void
    
    phoneNumber?: string;
    openingTimes?: {
        title: string;
        timeRange: string;
    }[];
}

const defaultPhoneNumber = '020 144 1511';
const defaultLocations = [
    {
        attributes: {
            externalId: '1',
            name: 'Lääkärikeskus Fenix Porvoo',
            address: 'Piispankatu 22',
            town: 'Porvoo',
            postalCode: '06100',
            openingHours: {
                TitleHoursPair: [
                    {Title: "MA", Hours: "08.00-19.00"},
                    {Title: "TI", Hours: "08.00-19.00"},
                    {Title: "KE", Hours: "08.00-19.00"},
                    {Title: "TO", Hours: "08.00-19.00"},
                    {Title: "PE", Hours: "08.00-16.00"},
                    {Title: "LA", Hours: "10.00-14.00"},
                    {Title: "SU", Hours: "Suljettu"}
                ]
            },
            phoneNumber: '020 144 1511'
        }
    },
    {
        attributes: {
            externalId: '3',
            name: 'Lääkärikeskus Fenix Porvoo Tori',
            address: 'Rauhankatu 29',
            town: 'Porvoo',
            postalCode: '06100',
            openingHours: {
                TitleHoursPair: [
                    {Title: "MA", Hours: "08.00-19.00"},
                    {Title: "TI", Hours: "08.00-16.00"},
                    {Title: "KE", Hours: "08.00-17.00"},
                    {Title: "TO", Hours: "08.00-19.00"},
                    {Title: "PE", Hours: "08.00-15.00"},
                    {Title: "LA", Hours: "Suljettu"},
                    {Title: "SU", Hours: "Suljettu"}
                ]
            },
            phoneNumber: '020 144 1511'
        }
    },
    {
        attributes: {
            externalId: '4',
            name: 'Lääkärikeskus Fenix Loviisa',
            address: 'Kuningattarenkatu 11',
            town: 'Loviisa',
            postalCode: '07900',
            openingHours: {
                TitleHoursPair: [
                    {Title: "MA", Hours: "08.00-16.00"},
                    {Title: "TI", Hours: "08.00-16.00"},
                    {Title: "KE", Hours: "08.00-16.00"},
                    {Title: "TO", Hours: "08.00-16.00"},
                    {Title: "PE", Hours: "08.00-15.00"},
                    {Title: "LA", Hours: "Suljettu"},
                    {Title: "SU", Hours: "Suljettu"}
                ]
            },
            phoneNumber: '020 144 1514'
        }
    },
    { 
        attributes: {
            externalId: '5',
            name: 'Mäntsälä',
            address: 'Karhukuja 1',
            town: 'Mäntsälä',
            postalCode: '04600',
            openingHours: {
                TitleHoursPair: [
                    {Title: "MA", Hours: "08.00-16.00"},
                    {Title: "TI", Hours: "08.00-16.00"},
                    {Title: "KE", Hours: "08.00-16.00"},
                    {Title: "TO", Hours: "08.00-16.00"},
                    {Title: "PE", Hours: "08.00-15.00"},
                    {Title: "LA", Hours: "Suljettu"},
                    {Title: "SU", Hours: "Suljettu"}
                ]
            },
            phoneNumber: '020 144 1510'
        }
    },
    {
        attributes: {
            externalId: '2',
            name: 'Lääkärikeskus Fenix Helsinki',
            address: 'Puistokatu 3A',
            town: 'Helsinki',
            postalCode: '00140',
            openingHours: null,
            phoneNumber: '020 144 1511'
        }
    },
] satisfies LocationsData[];
const defaultOpeningTimes = [
    {
        title: Localizer.contactSupportPageMoWeTh,
        timeRange: '08:00-19:00',
    },
    {
        title: Localizer.contactSupportPageTuFr,
        timeRange: '08:00-16:00',
    },
    {
        title: Localizer.contactSupportPageSa,
        timeRange: '10:00-14:00',
    }
];

interface QuickLink {
  label: string;
  pageRoute: PageRoute;
}

export const Footer = (props: FooterProps) => {
    const {locations, openingTimes, phoneNumber } = props;
    
    const [locationsData, setLocationsData] = useState<LocationsData[] | null>(null)
    const [googlePlayStoreLink, setGooglePlayStoreLink] = useState("https://play.google.com/store/apps/details?id=fi.fenix.digitalhealth&pcampaignid=web_share");
    const [appleStoreLink, setAppleStoreLink] = useState("https://apps.apple.com/fi/app/digifenix/id6677027323");
    
    useEffect(  () => {
        const fetchData = async () => {
            const locationData: { data?: LocationsData[] } = await ApiProvider.getAsync("/api/Application/GetDynamicPageContent?contentType=locations&deepPopulate=openingHours&sort=externalId", null);
            if (locationData?.data) {
                const order = ['1', '3', '4', '5', '2'];
                const sorted = _.sortBy(locationData.data, function(obj){
                    return _.indexOf(order, obj.attributes.externalId);
                });
                setLocationsData(sorted)
            }

            const digiClinicData: DynamicPageResponseSingleType = await ApiProvider.getAsync(
                "/api/Application/GetDynamicPageContent?contentType=digi-clinic-page", null
            );
            setGooglePlayStoreLink(prev => String(digiClinicData.data?.attributes?.GooglePlayStoreLink ?? prev));
            setAppleStoreLink(prev => String(digiClinicData.data?.attributes?.AppStoreLink ?? prev));
        }
        fetchData();
    }, [])

    // Must be inside the Footer component for localization switching to work 
    const quickLinks: QuickLink[] = [
    {
      label: Localizer.openingHoursPageTitle,
      pageRoute: PageDefinitions.openingHoursRoute,
    },
    {
      label: Localizer.contactSupportPageTitle,
      pageRoute: PageDefinitions.contactSupportRoute,
    },
    {
      label: Localizer.companyPageTitle,
      pageRoute: PageDefinitions.companyRoute,
    },
    {
      label: Localizer.newslettersPageSubscriptionSectionTitle,
      pageRoute: PageDefinitions.newslettersRoute,
    },
    {
      label: Localizer.companyPageFeedback,
      pageRoute: PageDefinitions.feedbackRoute,
    },
    {
      label: Localizer.pricingPageTitle,
      pageRoute: PageDefinitions.pricingRoute,
    },
    {
      label: Localizer.footerQuickLinksBilling,
      pageRoute: PageDefinitions.servicePageView.route({ params: { type: "service", id: Number(Localizer.footerQuickLinksBillingServiceId) }}),
    },
    {
      label: Localizer.companyPagePrivacyPolicy,
      pageRoute: PageDefinitions.privacyPolicyRoute,
    },
  ];
    
    const openLocation = async (id: string) => {
        await PageRouteProvider.redirectAsync(
            PageDefinitions.location.route({
                params: {selectedLocation: id}
            })
        )
    }

    const redirectToUrl = async (url: string) => {
        if(url) {
            window.open(url, '_blank')?.focus();
        }
    }
    
    return (
        <footer className={styles.container}>
            <div className={styles.content}>
                <div className={styles.gridItemServicePoints}>
                    <h4>{Localizer.footerServicePointsTitle}</h4>
                    <div className={styles.locations}>
                        {
                            (locationsData ?? defaultLocations).map((location, index) => (
                                <div key={index} className={styles.location}>
                                    <div>
                                        <Button
                                            type={ButtonType.Text}
                                            label={`Lääkärikeskus Fenix ${location.attributes.name} »`}
                                            onClick={async () => {
                                                location.attributes.externalId ?
                                                openLocation(location.attributes.externalId)
                                                    :
                                                    null
                                            }}
                                            className={styles.locationName}
                                        />
                                    </div>
                                    <div>{location.attributes.address}</div>
                                    <div className={styles.locationAddress}>
                                        <span>{location.attributes.postalCode}</span>
                                        <span>{location.attributes.town}</span>
                                    </div>
                                    {(location.attributes.phoneNumber && location.attributes.phoneNumber?.length > 0) && 
                                    <div className={styles.phoneNumber}>
                                        <span><Icon name='phone'/></span>
                                        <span>{Localizer.footerPhoneShortened}</span>
                                        <span>
                                            <a href={`tel:${location.attributes.phoneNumber?.replace(/\s+/g, '')}`}>
                                                {location.attributes.phoneNumber}
                                            </a>
                                        </span>
                                    </div>
                                    }
                                </div>
                            ))
                        }
                    </div>
                    <div>{Localizer.contactPhoneOpeningHours}</div>
                </div>
                <div className={styles.gridItemQuickLinks}>
                  <h4>{Localizer.footerQuickLinks}</h4>
                  <ul className={styles.quickLinks}>
                    {quickLinks.map(({ label, pageRoute }, index) => (
                      <li key={index}>
                        <Button
                            type={ButtonType.Text}
                            label={label}
                            onClick={async () => { await PageRouteProvider.redirectAsync(pageRoute) }}
                            className={styles.link}
                        />
                      </li>
                    ))}
                  </ul>
                  <h5>{Localizer.footerDownloadApp}</h5>
                  <div className={styles.appLinksDiv}>
                    <div className={styles.appLinkButtonDiv}>
                      <img onClick={() => redirectToUrl(googlePlayStoreLink)} src={PlayStoreBadge} alt="PlayStoreLink"/>
                    </div>
                    <div className={styles.appLinkButtonDiv}>
                      <img onClick={() => redirectToUrl(appleStoreLink)} src={AppStoreBadge} alt="AppStoreLink"/>
                    </div>
                  </div>
                </div>
            </div>
        </footer>
    );
};